import { Injectable } from '@angular/core';
import { AccountSettingsGateway } from '@domain/models/account-settings/gateway/account-settings.gateway';
import { AlertService } from '../alert-service/alert.service';
import { HttpClient } from '@angular/common/http';
import { PersonalInformation } from '@domain/models/account-settings/personal-information.model';
import { IAPIResponse } from '@domain/interfaces/apis/api-response.interface';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from '@environments/environment';
import { AlertPosition, AlertType } from '@domain/enums/alert/alert-type.enum';
import { PasswordChange } from '@domain/models/account-settings/password-change.model';
import { CypherService } from '../cypher-service/cypher.service';
import { CreditCard } from '@domain/models/account-settings/credit-card.model';
import { User } from '@domain/models/account-settings/user.model';
import { LoadingType } from '@domain/enums/alert/loading-type.enum';

@Injectable({
  providedIn: 'root',
})
export class AccountSettingsService extends AccountSettingsGateway {
  constructor(
    private _alertService: AlertService,
    private _http: HttpClient,
    private _cypherService: CypherService
  ) {
    super();
  }

  public getPersonalInformation(userId: number): Observable<User> {
    return this._http.get<User>(`${environment.memberPortalServiceURL.replace('{{brandDomain}}', window.location.host)}/member/personalinformation?userId=` + userId).pipe(
      map((apiResponse: User) => {
        this._alertService.showAlert({
          type: apiResponse.responseCode == 110 ? AlertType.Success : AlertType.Error,
          title: apiResponse.responseCode == 110 ? 'Success' : 'Error',
          description: apiResponse?.message,
          position: AlertPosition.TopCenter,
        });
        return apiResponse;
      }),
      catchError((e) => {
        console.log(e);
        this._alertService.showAlert({
          type: AlertType.Error,
          title: 'Error',
          description: e?.error?.Message,
          position: AlertPosition.TopCenter,
        });
        return of();
      })
    );
  }

  public updatePersonalInformation(personalInformation: PersonalInformation): Observable<IAPIResponse> {
    this._alertService.setLoading(true, LoadingType.Default, ['Updating personal information...']);
    return this._http.put<IAPIResponse>(`${environment.memberPortalServiceURL.replace('{{brandDomain}}', window.location.host)}/member/personalinformation`, personalInformation).pipe(
      map((apiResponse: IAPIResponse) => {
        this._alertService.setLoading(false);
        this._alertService.showAlert({
          type: apiResponse.responseCode == 105 ? AlertType.Success : AlertType.Error,
          title: apiResponse.responseCode == 105 ? 'Success' : 'Error',
          description: apiResponse?.message,
          position: AlertPosition.TopCenter,
        });
        return apiResponse;
      }),
      catchError((e) => {
        console.log(e);
        this._alertService.setLoading(false);
        this._alertService.showAlert({
          type: AlertType.Error,
          title: 'Error',
          description: e?.error?.Message,
          position: AlertPosition.TopCenter,
        });
        return of();
      })
    );
  }

  public updateBillingInformation(creditCard: CreditCard): Observable<IAPIResponse> {
    this._alertService.setLoading(true, LoadingType.Default, ['Updating billing information...']);
    return this._http.put<IAPIResponse>(`${environment.memberPortalServiceURL.replace('{{brandDomain}}', window.location.host)}/member/billing`, {
      userId: creditCard.userId,
      paySourceId: creditCard.paySourceId,
      RunCardValidationInKonnektive: creditCard.RunCardValidationInKonnektive,
      creditCardNumber: this._cypherService.encryptText(creditCard?.creditCardNumber?.toString()!),
      monthExpiration: this._cypherService.encryptText(creditCard?.monthExpiration!),
      yearExpiration: this._cypherService.encryptText(creditCard?.yearExpiration!),
      securityCode: this._cypherService.encryptText(creditCard?.securityCode?.toString()!),
    }).pipe(map((apiResponse: IAPIResponse) => {
        this._alertService.setLoading(false);
        this._alertService.showAlert({
          type: apiResponse.responseCode == 111 ? AlertType.Success : AlertType.Error,
          title: apiResponse.responseCode == 111 ? 'Success' : 'Error',
          description: apiResponse?.message,
          position: AlertPosition.TopCenter,
        });
        return apiResponse;
      }),
      catchError((e) => {
        console.log(e);
        this._alertService.setLoading(false);
        this._alertService.showAlert({
          type: AlertType.Error,
          title: 'Error',
          description: e?.error?.Message,
          position: AlertPosition.TopCenter,
        });
        return of();
      })
    );
  }

  public updatePassword(passwordChange: PasswordChange): Observable<IAPIResponse> {
    this._alertService.setLoading(true, LoadingType.Default, ['Updating password...']);
    passwordChange.currentPassword = this._cypherService.encryptText(passwordChange?.currentPassword!);
    passwordChange.newPassword = this._cypherService.encryptText(passwordChange?.newPassword!);
    passwordChange.confirmPassword = this._cypherService.encryptText(passwordChange?.confirmPassword!);
    return this._http.put<IAPIResponse>(`${environment.memberPortalServiceURL.replace('{{brandDomain}}', window.location.host)}/auth/password`, passwordChange).pipe(
      map((apiResponse: IAPIResponse) => {
        this._alertService.setLoading(false);
        this._alertService.showAlert({
          type: apiResponse.responseCode == 108 ? AlertType.Success : AlertType.Error,
          title: apiResponse.responseCode == 108 ? 'Success' : 'Error',
          description: apiResponse?.message,
          position: AlertPosition.TopCenter,
        });
        return apiResponse;
      }),
      catchError((e) => {
        console.log(e);
        this._alertService.setLoading(false);
        this._alertService.showAlert({
          type: AlertType.Error,
          title: 'Error',
          description: e?.error?.Message,
          position: AlertPosition.TopCenter,
        });
        return of();
      })
    );
  }
}
